import { useMemo } from 'react';

import sumBy from 'lodash/sumBy';

import {
  Box,
  ChartContainer,
  ChartTooltip,
  Line,
  MetricsBox,
  Skeleton,
  Stack,
  tokens,
  Typography,
  YAxis,
} from '@cast/design-system';
import { SeeMoreButton } from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';

import { InventoryListItem } from '../../../_components/ui/InventoryListItem';
import { InventoryWorkloadNameLink } from '../../../_components/ui/InventoryWorkloadNameLink';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { WORKLOAD_CATEGORIES } from '../../../_constants';
import { buildInventoryWorkloadCostItemId } from '../../../_utils/buildInventoryWorkloadCostItemId';
import { useInventoryWorkloadsDashboardNavigation } from '../_hooks/useInventoryWorkloadsDashboardNavigation';
import { COMPUTE_COST_SORT } from '../_utils/dashboardListNavigation';
import { useInventoryComputeCostReport } from './_hooks/useInventoryComputeCostReport';
import { useInventoryComputeCostSummaries } from './_hooks/useInventoryComputeCostSummaries';
import { formatCost } from './_utils/formatCost';
import { SparklineDatum } from './_types';

const TOP_SPENDERS_LIMIT = 4;

const ComputeCostCardSkeleton = () => (
  <Stack height="100%" gap={16} justifyContent="space-between">
    <Stack direction="row" alignItems="center" gap={12} sx={{ flexShrink: 0 }}>
      <MetricsBox
        testId="compute-cost-current-month"
        size="m"
        slots={{
          label: 'compute spend',
          value: '--',
        }}
        slotProps={{
          label: { color: tokens.theme.fg.structural.secondary },
          value: { variant: 'metriclg' },
        }}
      />
      <Box flex={1} height={32}>
        <Skeleton variant="rectangular" height={32} />
      </Box>
    </Stack>

    <Stack gap={16} flex="0 1 auto" minHeight={0}>
      <Typography
        variant="L10R"
        color="textSecondary"
        sx={{ textTransform: 'uppercase' }}
      >
        top workloads by spend
      </Typography>
      <Stack flex="0 1 auto" minHeight={0} overflow="auto">
        {Array.from({ length: TOP_SPENDERS_LIMIT }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            height={24}
            sx={{ my: 6 }}
            data-testid={`compute-cost-top-spender-skeleton-${index}`}
          />
        ))}
      </Stack>
    </Stack>
  </Stack>
);

export const ComputeCostCard = () => {
  const {
    datapoints,
    isLoading: isReportLoading,
    isFetching: isReportFetching,
    error: reportError,
    refetch: refetchReport,
  } = useInventoryComputeCostReport();
  const {
    workloads,
    isLoading: isSummariesLoading,
    isFetching: isSummariesFetching,
    error: summariesError,
    refetch: refetchSummaries,
  } = useInventoryComputeCostSummaries();
  const { navigateToComputeList, getWorkloadDashboardPath } =
    useInventoryWorkloadsDashboardNavigation();

  const isLoading = isReportLoading || isSummariesLoading;
  const isFetching = isReportFetching || isSummariesFetching;
  const hasError = !!(reportError || summariesError);

  const isCardLoading = isLoading || (hasError && isFetching);

  const refetch = () => {
    refetchReport();
    refetchSummaries();
  };

  const totalSpend = useMemo(
    () => sumBy(workloads, ({ cost }) => Number(cost?.totalCost) || 0),
    [workloads]
  );

  const topSpenders = useMemo(
    () =>
      [...workloads]
        .sort(
          (a, b) =>
            Number(b.cost?.totalCost || 0) - Number(a.cost?.totalCost || 0)
        )
        .slice(0, TOP_SPENDERS_LIMIT),
    [workloads]
  );

  const sparklineData: SparklineDatum[] = useMemo(
    () =>
      datapoints.map(({ timestamp, chartTimestampUnix, values }) => ({
        timestamp,
        chartTimestampUnix,
        value: Object.values(values || {}).reduce<number>(
          (sum, value) => sum + (value ?? 0),
          0
        ),
      })),
    [datapoints]
  );

  return (
    <WidgetLayout
      title="Compute cost"
      testId="compute-cost-card"
      headerDivider={false}
      headerEndContent={
        <SeeMoreButton
          onClick={() => navigateToComputeList(COMPUTE_COST_SORT)}
        />
      }
    >
      {isCardLoading ? (
        <ComputeCostCardSkeleton />
      ) : hasError ? (
        <FailedToLoad
          title="Failed to load compute cost"
          refresh={refetch}
          compact
        />
      ) : (
        <Stack height="100%" gap={16} justifyContent="space-between">
          <Stack
            direction="row"
            alignItems="center"
            gap={12}
            data-testid="compute-cost-card-summary"
            sx={{ flexShrink: 0 }}
          >
            <MetricsBox
              testId="compute-cost-current-month"
              size="m"
              slots={{
                label: 'compute spend',
                value: formatCost(totalSpend),
              }}
              slotProps={{
                label: { color: tokens.theme.fg.structural.secondary },
                value: { variant: 'metriclg' },
              }}
            />
            <Box flex={1} height={32}>
              <ChartContainer<SparklineDatum>
                data={sparklineData}
                isLoading={false}
                margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
                testId="compute-cost-sparkline"
              >
                <Line<SparklineDatum>
                  dataKey="value"
                  stroke={tokens.theme.data.grey400}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <ChartTooltip<SparklineDatum>
                  formatValue={(value) => formatCost(Number(value))}
                />
              </ChartContainer>
            </Box>
          </Stack>

          <Stack
            gap={16}
            flex="0 1 auto"
            minHeight={0}
            data-testid="compute-cost-card-details"
          >
            <Typography
              variant="L10R"
              color="textSecondary"
              sx={{ textTransform: 'uppercase' }}
            >
              top workloads by spend
            </Typography>
            <Stack flex="0 1 auto" minHeight={0} overflow="auto">
              {topSpenders.map((item, index) => {
                const workloadId = buildInventoryWorkloadCostItemId(item);

                return (
                  <InventoryListItem
                    key={workloadId}
                    testId={`compute-cost-top-spender-${item.workloadName}`}
                    size="sm"
                    divider={index < topSpenders.length - 1}
                    slots={{
                      startContent: null,
                      label: (
                        <InventoryWorkloadNameLink
                          to={getWorkloadDashboardPath(
                            workloadId,
                            WORKLOAD_CATEGORIES.COMPUTE
                          )}
                        >
                          {item.workloadName}
                        </InventoryWorkloadNameLink>
                      ),
                      endContent: formatCost(Number(item.cost?.totalCost)),
                    }}
                    slotProps={{
                      endContent: { variant: 'P12B' },
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
        </Stack>
      )}
    </WidgetLayout>
  );
};
